import { useSpecialPermissionStore } from "feature/specialpermissions/store";
import { useFormik } from "formik";
import { RequestSpecialPermission, SpecialPermission } from "interfaces";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { CreateSpecialPermission, UpdateSpecialPermission } from "services";
import * as Yup from "yup";
import { useListSpecialPermissions } from "../useListSpecialPermissions";

export const useModalAddSpecialPermissions = () => {
  const { item, isEdit, clearAll, setIsOpenModal } =
    useSpecialPermissionStore();
  const { t } = useTranslation();
  const { refetchSpecialPermission } = useListSpecialPermissions();

  const initialValues: SpecialPermission = {
    id: item?.id || 0,
    description: item?.description || "",
    state: item?.state || 1,
  };

  const validationSchema = Yup.object({
    description: Yup.string()
      .required(t("SpecialPermission.validation.required"))
      .min(3, t("SpecialPermission.validation.minLegth"))
      .max(200, t("SpecialPermission.validation.maxLegth"))
      .matches(
        /^[a-zA-ZÀ-ÿñÑ\s]+$/,
        t("SpecialPermission.validation.format")
      )
      .trim()
      .test('no-whitespace', t("SpecialPermission.validation.noWhitespace"), 
        value => value?.trim().length > 0
      ),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (isEdit && initialValues.id) {
          updateSpecialPermission(initialValues.id, values);
        } else {
          createSpecialPermission(values);
        }
      } catch (error) {
        throw error;
      } finally {
        formik.resetForm()
        setSubmitting(false);
      }
    },
  });

  const createSpecialPermission = async (data: SpecialPermission) => {
    try {
      const values: RequestSpecialPermission = {
        description: data.description,
      };
      const response = await CreateSpecialPermission(values);
      
      if (response.code === "200") {
        clearAll();
        setIsOpenModal(false);
        refetchSpecialPermission();
        toast.success(t("SpecialPermission.create.add"), { autoClose: 2000 });
      } else {
        clearAll();
        toast.error(t("SpecialPermission.create.error"), { autoClose: 2000 });
      }
    } catch (error) {
      toast.error(t("SpecialPermission.create.error"), { autoClose: 2000 });
    }finally{
      formik.resetForm();
    }
  };

  const updateSpecialPermission = async (
    id: number,
    data: SpecialPermission
  ) => {
    try {
      const values: RequestSpecialPermission = {
        description: data.description,
      };
      const response = await UpdateSpecialPermission(id, values);
      
      if (response.code === "200") {
        clearAll();
        setIsOpenModal(false);
        refetchSpecialPermission();
        toast.success(t("SpecialPermission.create.update"), { autoClose: 2000 });
      } else {
        clearAll();
        toast.error(t("SpecialPermission.create.error"), { autoClose: 2000 });
      }
    
    } catch (error) {
      toast.error(t("SpecialPermission.create.error"), { autoClose: 2000 });
    }
  };

  return { formik, t };
};
